// Signup Page with Stripe Elements
// Handles marketing site → app signup flow with payment collection

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { trpc } from '../../lib/trpc';

// Load Stripe outside of component to avoid recreating on every render
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

// ============================================================
// TYPES
// ============================================================

type Product = 'fra' | 'ebm' | 'bundle';
type Tier = 'basic' | 'premium' | 'elite' | 'enterprise';
type BillingCycle = 'monthly' | 'annual';

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  companyName: string;
}

interface PlanInfo {
  product: Product;
  tier: Tier;
  billingCycle: BillingCycle;
}

const SELF_SERVE_PRODUCT: Product = 'fra';
const SELF_SERVE_TIERS: Tier[] = ['basic', 'premium', 'elite'];
const CONTACT_SALES_URL = 'https://calculom.com/contact';

// ============================================================
// PRICING DISPLAY DATA (matches backend stripe-prices.ts)
// ============================================================

const TIER_NAMES: Record<Tier, string> = {
  basic: 'Basic',
  premium: 'Premium',
  elite: 'Elite',
  enterprise: 'Enterprise',
};

// Product-specific color themes
const PRODUCT_COLORS: Record<Product, {
  primary: string;      // bg color for summary card, primary buttons
  primaryHover: string; // hover state
  border: string;       // selected border
  bg: string;          // light background when selected
  text: string;        // text color for pricing
  badge: string;       // badge background (bundle "Save 20%")
  badgeText: string;   // badge text color
}> = {
  fra: {
    primary: 'bg-blue-600',
    primaryHover: 'hover:bg-blue-700',
    border: 'border-blue-600',
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    badge: 'bg-blue-100',
    badgeText: 'text-blue-700',
  },
  ebm: {
    primary: 'bg-teal-600',
    primaryHover: 'hover:bg-teal-700',
    border: 'border-teal-600',
    bg: 'bg-teal-50',
    text: 'text-teal-600',
    badge: 'bg-teal-100',
    badgeText: 'text-teal-700',
  },
  bundle: {
    primary: 'bg-amber-500',
    primaryHover: 'hover:bg-amber-600',
    border: 'border-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    badge: 'bg-amber-100',
    badgeText: 'text-amber-700',
  },
};

// Monthly price per month, Annual price per month (billed annually)
// Matches PRICE_TABLE in apps/api/src/config/stripe-prices.ts
const PRICES: Record<Product, Record<Tier, { monthly: number; annual: number }>> = {
  fra: {
    basic: { monthly: 299, annual: 249 },
    premium: { monthly: 719, annual: 599 },
    elite: { monthly: 1199, annual: 949 },
    enterprise: { monthly: 0, annual: 0 }, // Contact sales
  },
  ebm: {
    basic: { monthly: 59, annual: 49 },
    premium: { monthly: 119, annual: 99 },
    elite: { monthly: 239, annual: 199 },
    enterprise: { monthly: 0, annual: 0 },
  },
  bundle: {
    basic: { monthly: 78, annual: 65 },
    premium: { monthly: 147, annual: 121 },
    elite: { monthly: 286, annual: 235 },
    enterprise: { monthly: 0, annual: 0 },
  },
};

// Annual totals for display
const ANNUAL_TOTALS: Record<Product, Record<Tier, number>> = {
  fra: { basic: 2988, premium: 7188, elite: 11388, enterprise: 0 },
  ebm: { basic: 588, premium: 1188, elite: 2388, enterprise: 0 },
  bundle: { basic: 778, premium: 1450, elite: 2824, enterprise: 0 },
};

const TRIAL_DAYS = 15;

// ============================================================
// PAYMENT FORM COMPONENT
// ============================================================

interface PaymentFormProps {
  clientSecret: string;
  formData: SignupFormData;
  planInfo: PlanInfo;
  skipTrial: boolean;
  onSuccess: () => void;
  onError: (error: string) => void;
}

function PaymentForm({ clientSecret, formData, planInfo, skipTrial, onSuccess, onError }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const activateMutation = trpc.subscription.activateSubscription.useMutation({
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      onError(error.message);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      // Confirm the SetupIntent with Stripe
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/signup/complete',
        },
        redirect: 'if_required',
      });

      if (error) {
        onError(error.message || 'Payment method verification failed');
        setIsProcessing(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Activate the subscription via API
        activateMutation.mutate({
          setupIntentId: setupIntent.id,
          authProvider: 'email',
          password: formData.password,
        });
      } else {
        onError('Payment setup incomplete. Please try again.');
        setIsProcessing(false);
      }
    } catch (err) {
      onError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const price = PRICES[planInfo.product][planInfo.tier];
  const isAnnual = planInfo.billingCycle === 'annual';
  const displayPrice = isAnnual ? price.annual : price.monthly;
  const annualTotal = ANNUAL_TOTALS[planInfo.product][planInfo.tier];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Payment Method</h3>
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {!skipTrial && (
        <div className={`rounded-lg ${PRODUCT_COLORS[planInfo.product].bg} border ${PRODUCT_COLORS[planInfo.product].border} p-4`}>
          <div className={`flex items-center gap-2 ${PRODUCT_COLORS[planInfo.product].text}`}>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{TRIAL_DAYS}-Day Free Trial</span>
          </div>
          <p className={`text-sm ${PRODUCT_COLORS[planInfo.product].text} opacity-80 mt-2`}>
            You won't be charged until your trial ends. Cancel anytime.
          </p>
        </div>
      )}

      {skipTrial && (
        <div className="rounded-lg bg-gray-100 border border-gray-300 p-4">
          <div className="flex items-center gap-2 text-gray-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Immediate Billing</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {isAnnual
              ? `You'll be charged $${annualTotal}/yr today.`
              : `You'll be charged $${displayPrice}/mo today.`}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing || activateMutation.isPending}
        className={`w-full py-3 px-4 ${PRODUCT_COLORS[planInfo.product].primary} text-white font-semibold rounded-lg ${PRODUCT_COLORS[planInfo.product].primaryHover} disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
      >
        {isProcessing || activateMutation.isPending ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Processing...
          </span>
        ) : skipTrial ? (
          'Subscribe Now'
        ) : (
          `Start ${TRIAL_DAYS}-Day Free Trial`
        )}
      </button>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        Need to change when billing starts? Use the Back button above and update the trial option before submitting payment.
      </div>

      <p className="text-xs text-gray-500 text-center">
        {skipTrial ? (
          <>
            {isAnnual
              ? `You'll be charged $${annualTotal}/yr billed annually today (equivalent to $${displayPrice}/mo).`
              : `You'll be charged $${displayPrice}/mo today.`}
          </>
        ) : (
          <>
            {isAnnual
              ? `After your trial, you'll be charged $${annualTotal}/yr billed annually (equivalent to $${displayPrice}/mo).`
              : `After your trial, you'll be charged $${displayPrice}/mo.`}
            Cancel anytime before your trial ends to avoid charges.
          </>
        )}
      </p>
    </form>
  );
}

// ============================================================
// MAIN SIGNUP PAGE
// ============================================================

export function SignupPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get plan info from URL params (with defaults)
  const [planInfo, setPlanInfo] = useState<PlanInfo>({
    product: SELF_SERVE_PRODUCT,
    tier: SELF_SERVE_TIERS.includes(searchParams.get('tier') as Tier)
      ? (searchParams.get('tier') as Tier)
      : 'premium',
    billingCycle: (searchParams.get('cycle') as BillingCycle) || 'monthly',
  });

  // Form state
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<SignupFormData>();

  const password = watch('password');

  // Step management
  const [step, setStep] = useState<'info' | 'payment' | 'success'>('info');
  const [formData, setFormData] = useState<SignupFormData | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [skipTrial, setSkipTrial] = useState(false);

  // Create SetupIntent mutation
  const createSetupIntentMutation = trpc.subscription.createSetupIntent.useMutation({
    onSuccess: (data) => {
      setClientSecret(data.clientSecret);
      setStep('payment');
    },
    onError: (error) => {
      setSubmitError(error.message);
    },
  });

  // Update URL when plan changes
  useEffect(() => {
    const newParams = new URLSearchParams();
    newParams.set('tier', planInfo.tier);
    newParams.set('cycle', planInfo.billingCycle);
    setSearchParams(newParams, { replace: true });
  }, [planInfo, setSearchParams]);

  // Handle form submission (step 1 → step 2)
  const onSubmitInfo = (data: SignupFormData) => {
    setFormData(data);
    setSubmitError(null);

    // Create SetupIntent to get client secret for Stripe Elements
    createSetupIntentMutation.mutate({
      email: data.email,
      name: data.name,
      companyName: data.companyName,
      product: planInfo.product,
      tier: planInfo.tier,
      billingCycle: planInfo.billingCycle,
      skipTrial,
    });
  };

  // Handle successful signup
  const handleSuccess = () => {
    setStep('success');
  };

  // Handle payment error
  const handlePaymentError = (error: string) => {
    setSubmitError(error);
  };

  // Get pricing display
  const price = PRICES[planInfo.product][planInfo.tier];
  const isAnnual = planInfo.billingCycle === 'annual';
  const displayPrice = isAnnual ? price.annual : price.monthly;
  const annualTotal = ANNUAL_TOTALS[planInfo.product][planInfo.tier];
  const annualSavings = planInfo.billingCycle === 'annual'
    ? Math.round((1 - annualTotal / (price.monthly * 12)) * 100)
    : 0;

  // Available tiers for self-serve selection
  const tiers = SELF_SERVE_TIERS;

  // Success view
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Calculom!</h1>
          <p className="text-gray-600 mb-6">
            {skipTrial ? (
              <>Your account has been created and your subscription is now active. Check your email for next steps.</>
            ) : (
              <>Your account has been created and your {TRIAL_DAYS}-day free trial has started. Check your email for next steps.</>
            )}
          </p>
          <Link
            to="/login"
            className="inline-block py-3 px-6 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Sign In to Get Started
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/">
            <img src="/calculom.png" alt="Calculom" className="w-[110px] h-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Already have an account?</span>
            <Link
              to="/login"
              className="px-4 py-2 border-2 border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
            >
              Log in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-blue-600 text-white py-12 px-4 text-center">
        <h1 className="text-4xl font-bold mb-4">Start Your {TRIAL_DAYS}-Day Free Trial</h1>
        <p className="text-xl opacity-90 max-w-2xl mx-auto mb-6">
          Full access to all features during your trial. No commitment required.
        </p>
        <div className="flex justify-center gap-6 flex-wrap text-sm">
          <span className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Credit card required
          </span>
          <span className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Cancel anytime
          </span>
          <span className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Full feature access
          </span>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Plan Selection */}
          <div className="space-y-6">
            {/* Tier Selection */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Choose Your Tier</h2>
              <p className="text-sm text-gray-600 mb-4">
                All self-serve plans include Calculom's Excel-based reporting platform. Enterprise deployments go through sales.
              </p>
              <div className="space-y-3">
                {tiers.map((tier) => {
                  const tierPrice = PRICES[planInfo.product][tier];
                  const tierAnnualTotal = ANNUAL_TOTALS[planInfo.product][tier];
                  const colors = PRODUCT_COLORS[planInfo.product];
                  const isSelected = planInfo.tier === tier;
                  return (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setPlanInfo((p) => ({ ...p, tier }))}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? `${colors.border} ${colors.bg}`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-900">{TIER_NAMES[tier]}</span>
                        <div className="text-right">
                        <span className={`font-semibold ${colors.text}`}>
                            ${planInfo.billingCycle === 'monthly' ? tierPrice.monthly : tierAnnualTotal}
                            <span className="text-sm text-gray-500">
                              {planInfo.billingCycle === 'monthly' ? '/mo' : '/yr'}
                            </span>
                          </span>
                          {planInfo.billingCycle === 'annual' && (
                            <div className="text-xs text-gray-500">
                              ${tierPrice.annual}/mo billed annually
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                <a
                  href={CONTACT_SALES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full rounded-lg border-2 border-dashed border-gray-300 p-4 transition-all hover:border-blue-400 hover:bg-blue-50"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium text-gray-900">{TIER_NAMES.enterprise}</div>
                      <p className="mt-1 text-sm text-gray-600">
                        Contact sales for enterprise deployment, onboarding, and custom rollout needs.
                      </p>
                    </div>
                    <span className="shrink-0 rounded-lg border border-blue-600 px-3 py-2 text-sm font-semibold text-blue-600">
                      Contact Sales
                    </span>
                  </div>
                </a>
              </div>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Cycle</h2>
              <div className="flex items-center justify-center gap-4">
                <span className={planInfo.billingCycle === 'monthly' ? 'font-semibold' : 'text-gray-500'}>
                  Monthly
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPlanInfo((p) => ({
                      ...p,
                      billingCycle: p.billingCycle === 'monthly' ? 'annual' : 'monthly',
                    }))
                  }
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    planInfo.billingCycle === 'annual' ? PRODUCT_COLORS[planInfo.product].primary : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      planInfo.billingCycle === 'annual' ? 'translate-x-7' : ''
                    }`}
                  />
                </button>
                <span className={planInfo.billingCycle === 'annual' ? 'font-semibold' : 'text-gray-500'}>
                  Annual
                  {annualSavings > 0 && (
                    <span className={`ml-2 px-2 py-0.5 ${PRODUCT_COLORS[planInfo.product].badge} ${PRODUCT_COLORS[planInfo.product].badgeText} text-xs font-semibold rounded`}>
                      Save {annualSavings}%
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Selected Plan Summary */}
            <div className={`${PRODUCT_COLORS[planInfo.product].primary} rounded-xl p-6 text-white transition-colors`}>
              <div className="text-center">
                <div className="text-sm opacity-80 mb-1">Selected Plan</div>
                <div className="text-lg font-medium mb-2">
                  Calculom {TIER_NAMES[planInfo.tier]}
                </div>
                <div className="text-4xl font-bold">
                  ${isAnnual ? annualTotal : displayPrice}
                  <span className="text-lg font-normal opacity-80">{isAnnual ? '/yr' : '/mo'}</span>
                </div>
                {isAnnual && (
                  <div className="text-sm opacity-80 mt-1">
                    ${displayPrice}/mo billed annually
                  </div>
                )}
                <div className="mt-3 text-sm opacity-80">
                  {skipTrial ? (
                    <>Billed {planInfo.billingCycle} starting today</>
                  ) : (
                    <>{TRIAL_DAYS}-day free trial, then billed {planInfo.billingCycle}</>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Registration Form or Payment */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            {step === 'info' ? (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Create Your Account</h2>

                {submitError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmitInfo)} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Work Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="john@company.com"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email',
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John Smith"
                      {...register('name', { required: 'Name is required' })}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      id="companyName"
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Acme Inc."
                      {...register('companyName', { required: 'Company name is required' })}
                    />
                    {errors.companyName && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="At least 8 characters"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Password must be at least 8 characters' },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                          message: 'Password must contain uppercase, lowercase, and a number',
                        },
                      })}
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Re-enter your password"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => value === password || 'Passwords do not match',
                      })}
                    />
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={createSetupIntentMutation.isPending}
                    className={`w-full py-3 px-4 ${PRODUCT_COLORS[planInfo.product].primary} text-white font-semibold rounded-lg ${PRODUCT_COLORS[planInfo.product].primaryHover} disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
                  >
                    {createSetupIntentMutation.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </span>
                    ) : skipTrial ? (
                      'Continue to Payment'
                    ) : (
                      `Start ${TRIAL_DAYS}-Day Free Trial`
                    )}
                  </button>

                  {/* Skip Trial Toggle */}
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setSkipTrial(!skipTrial)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        skipTrial ? PRODUCT_COLORS[planInfo.product].primary : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                          skipTrial ? 'translate-x-5' : ''
                        }`}
                      />
                    </button>
                    <span className="text-sm text-gray-600">
                      Skip free trial and subscribe immediately
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    By signing up, you agree to our{' '}
                    <a href="https://calculom.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="https://calculom.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Privacy Policy
                    </a>
                  </p>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('info');
                      setClientSecret(null);
                      setSubmitError(null);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    ← Back
                  </button>
                </div>

                {submitError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                {clientSecret && formData && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: 'stripe',
                        variables: {
                          colorPrimary: '#2563eb',
                          borderRadius: '8px',
                        },
                      },
                    }}
                  >
                    <PaymentForm
                      clientSecret={clientSecret}
                      formData={formData}
                      planInfo={planInfo}
                      skipTrial={skipTrial}
                      onSuccess={handleSuccess}
                      onError={handlePaymentError}
                    />
                  </Elements>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm opacity-80">© {new Date().getFullYear()} Calculom. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://calculom.com/privacy" className="text-sm opacity-80 hover:opacity-100">Privacy</a>
            <a href="https://calculom.com/terms" className="text-sm opacity-80 hover:opacity-100">Terms</a>
            <a href="https://calculom.com/contact" className="text-sm opacity-80 hover:opacity-100">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
